/**
 * Restock sign up form.
 *
 * The Notify Me button and its dialog belong to notify-me-button.js; this file
 * only fills the form inside that dialog and posts the sign up. It listens to
 * the button's `notify-me:open` event without cancelling it, so the dialog, the
 * focus trap, Escape, the overlay click and the focus return all stay where they
 * were built.
 *
 * The sign up goes straight to Klaviyo's public client side back in stock
 * endpoint. Nothing in the request asks for marketing email: it carries the
 * shopper's address, the EMAIL channel and the variant, and no consent or
 * subscription of any kind.
 */
(function () {
  'use strict';

  // Quick view copies the buy area scripts into head on every open, so a second
  // run must not install a second set of listeners.
  if (window.themeNotifyMeForm && typeof window.themeNotifyMeForm.refresh === 'function') {
    window.themeNotifyMeForm.refresh();
    return;
  }

  var ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  var REVISION = '2024-10-15';
  // Klaviyo's own id format for a Shopify variant.
  var VARIANT_PREFIX = '$shopify:::$default:::';
  // Deliberately loose: the address is checked properly by Klaviyo, this only
  // catches the shapes that cannot be an address at all.
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var SIZE_OPTION_PATTERN = /size|taille|gr(ö|o)(ss|ß)e|talla|taglia|maat|beden/i;

  var FORM_SELECTOR = '[data-notify-me-form]';
  var DIALOG_SELECTOR = '[data-notify-me-dialog]';

  // Product JSON keyed by handle, and the variant ids each one covers, so an
  // open can be answered without going back to the network.
  var productsByHandle = {};
  var handleByVariantId = {};

  function normalise(product) {
    if (!product || !product.variants || !product.variants.length) return null;

    var optionNames = (product.options || []).map(function (option) {
      return typeof option === 'string' ? option : (option && option.name) || '';
    });

    var variants = product.variants.map(function (variant) {
      var values = variant.options;
      if (!values || !values.length) {
        values = [variant.option1, variant.option2, variant.option3].filter(function (value) {
          return value !== null && typeof value !== 'undefined';
        });
      }
      return {
        id: String(variant.id),
        available: variant.available !== false,
        title: variant.title || '',
        values: values
      };
    });

    if (!optionNames.length) {
      optionNames = variants[0].values.map(function () { return ''; });
    }

    // The option the dropdown lists. Named where the product says so, last one
    // otherwise, which is where size sits on every product in this catalogue.
    var sizeIndex = -1;
    optionNames.forEach(function (name, index) {
      if (sizeIndex === -1 && SIZE_OPTION_PATTERN.test(name)) sizeIndex = index;
    });
    if (sizeIndex === -1) sizeIndex = Math.max(optionNames.length - 1, 0);

    return {
      handle: product.handle || '',
      title: product.title || '',
      sizeIndex: sizeIndex,
      variants: variants
    };
  }

  function remember(product) {
    var normalised = normalise(product);
    if (!normalised || !normalised.handle) return normalised;

    productsByHandle[normalised.handle] = normalised;
    normalised.variants.forEach(function (variant) {
      handleByVariantId[variant.id] = normalised.handle;
    });
    return normalised;
  }

  // The blob Liquid rendered next to the form: the colour the page was built
  // with, correct before any script has run.
  function readInlineProducts() {
    Array.prototype.slice.call(document.querySelectorAll('[data-notify-me-product]')).forEach(function (node) {
      if (node.getAttribute('data-notify-me-read') === 'true') return;
      node.setAttribute('data-notify-me-read', 'true');
      try {
        remember(JSON.parse(node.textContent));
      } catch (error) {
        // A product with no variants would be the only way here; the form falls
        // back to fetching the product JSON.
      }
    });
  }

  // A sibling colour swap replaces the size swatches and stamps the destination
  // handle on each one, so this is the handle of the colour actually on show.
  function displayedHandle(scope) {
    var radio = (scope || document).querySelector('fieldset[data-handle="size"] input[data-product-handle]') ||
      document.querySelector('fieldset[data-handle="size"] input[data-product-handle]');
    return (radio && radio.getAttribute('data-product-handle')) || '';
  }

  function productFor(variantId, handle) {
    if (handle && productsByHandle[handle]) {
      var byHandle = productsByHandle[handle];
      var covers = !variantId || byHandle.variants.some(function (variant) {
        return variant.id === variantId;
      });
      // A known handle whose cached copy does not carry this variant is a cache
      // miss, not a reason to answer with a different colour's product: fall
      // through to the variant lookup, which knows which colour owns it.
      if (covers) return byHandle;
    }
    if (variantId && handleByVariantId[variantId]) {
      return productsByHandle[handleByVariantId[variantId]];
    }
    return null;
  }

  function fetchProduct(handle) {
    // Shopify publishes the locale root for the storefront the shopper is on;
    // without it the fetch drops out of the market's locale and 404s.
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return window.fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('product json ' + response.status);
      return response.json();
    }).then(function (data) {
      return remember(data);
    });
  }

  // The sold out sizes of the colour on show: every variant that cannot be
  // bought and that matches the selected variant on every option but size.
  function soldOutSizes(product, variantId) {
    if (!product) return [];

    var current = product.variants.filter(function (variant) {
      return variant.id === variantId;
    })[0];

    // Without the selected variant there is no colour to filter by, and listing
    // every sold out variant of the product would offer other colours' sizes.
    if (!current) return [];

    return product.variants.filter(function (variant) {
      if (variant.available) return false;
      return variant.values.every(function (value, index) {
        return index === product.sizeIndex || value === current.values[index];
      });
    }).map(function (variant) {
      return {
        id: variant.id,
        label: variant.values[product.sizeIndex] || variant.title
      };
    });
  }

  // Per form request bookkeeping: a generation token that every close, reopen or
  // new submit invalidates, and a flag for a sign up that is still in flight.
  function stateFor(form) {
    if (!form.notifyMeState) form.notifyMeState = { generation: 0, pending: false };
    return form.notifyMeState;
  }

  function parts(form) {
    return {
      form: form,
      dialog: form.closest(DIALOG_SELECTOR),
      email: form.querySelector('[data-notify-me-email]'),
      emailError: form.querySelector('[data-notify-me-email-error]'),
      size: form.querySelector('[data-notify-me-size]'),
      submit: form.querySelector('[data-notify-me-submit]'),
      idle: form.querySelector('[data-notify-me-submit-idle]'),
      pending: form.querySelector('[data-notify-me-submit-pending]'),
      formError: form.querySelector('[data-notify-me-form-error]'),
      success: form.parentNode && form.parentNode.querySelector('[data-notify-me-success]')
    };
  }

  function clearMessages(nodes) {
    if (nodes.emailError) nodes.emailError.textContent = '';
    if (nodes.formError) nodes.formError.textContent = '';
    if (nodes.success) nodes.success.textContent = '';
    if (nodes.email) nodes.email.removeAttribute('aria-invalid');
  }

  function setPending(nodes, pending) {
    if (nodes.submit) {
      // A second tap while the request is in flight cannot start a second one.
      if (pending) {
        nodes.submit.setAttribute('disabled', 'disabled');
      } else {
        nodes.submit.removeAttribute('disabled');
      }
      nodes.submit.setAttribute('aria-busy', pending ? 'true' : 'false');
    }
    if (nodes.idle) {
      if (pending) {
        nodes.idle.setAttribute('hidden', '');
      } else {
        nodes.idle.removeAttribute('hidden');
      }
    }
    if (nodes.pending) {
      if (pending) {
        nodes.pending.removeAttribute('hidden');
      } else {
        nodes.pending.setAttribute('hidden', '');
      }
    }
  }

  function fillSizes(nodes, product, variantId, pending) {
    if (!nodes.size) return;

    var sizes = soldOutSizes(product, variantId);
    nodes.size.innerHTML = '';

    if (!sizes.length) {
      var empty = document.createElement('option');
      empty.value = '';
      empty.textContent = nodes.form.getAttribute('data-no-sizes') || '';
      nodes.size.appendChild(empty);
      nodes.size.setAttribute('disabled', 'disabled');
      if (nodes.submit) nodes.submit.setAttribute('disabled', 'disabled');
      return;
    }

    sizes.forEach(function (size) {
      var option = document.createElement('option');
      option.value = size.id;
      option.textContent = size.label;
      // The size the shopper has selected on the page starts selected, and can
      // be changed to any other sold out size in this colour.
      if (size.id === variantId) option.selected = true;
      nodes.size.appendChild(option);
    });

    nodes.size.removeAttribute('disabled');
    // A sign up started before this reopen is still running, so the button stays
    // disabled until that request settles.
    if (nodes.submit && !pending) nodes.submit.removeAttribute('disabled');
  }

  // Back to a clean form: no address, no previous success or error, the sizes of
  // the colour on show and the size selected on the page preselected.
  function reset(form, context) {
    var nodes = parts(form);
    var state = stateFor(form);
    var variantId = context && context.variantId ? String(context.variantId) : '';

    // Anything already in flight belongs to the form as it was; its answer must
    // not be written onto this one.
    state.generation += 1;
    var generation = state.generation;

    nodes.form.removeAttribute('hidden');
    if (nodes.email) nodes.email.value = '';
    clearMessages(nodes);
    setPending(nodes, state.pending);

    readInlineProducts();

    var scope = context && context.button ? context.button.closest('product-form') : null;
    var handle = displayedHandle(scope) || (context && context.productHandle) || '';

    var product = productFor(variantId, handle);
    if (product) {
      fillSizes(nodes, product, variantId, state.pending);
      return;
    }

    // Nothing on the page covers this variant yet - a colour swap that ran
    // before this script cached anything - so ask the storefront for it.
    if (nodes.size) {
      nodes.size.setAttribute('disabled', 'disabled');
    }
    if (nodes.submit) nodes.submit.setAttribute('disabled', 'disabled');

    if (!handle) return;

    // The previous colour's sizes must not sit there looking selectable while
    // the storefront is asked for this one.
    if (nodes.size) {
      nodes.size.innerHTML = '';
      var loading = document.createElement('option');
      loading.value = '';
      loading.textContent = nodes.form.getAttribute('data-size-loading') || '';
      nodes.size.appendChild(loading);
    }

    fetchProduct(handle).then(function (fetched) {
      if (state.generation !== generation) return;
      if (!document.contains(nodes.form)) return;
      fillSizes(nodes, fetched, variantId, state.pending);
    }).catch(function () {
      if (state.generation !== generation) return;
      if (!document.contains(nodes.form)) return;
      if (nodes.formError) nodes.formError.textContent = strings(nodes).requestError;
    });
  }

  // Every shopper facing string is rendered onto the form by Liquid, so this
  // file carries no copy of its own.
  function strings(nodes) {
    var form = nodes.form;
    return {
      invalidEmail: (form && form.getAttribute('data-invalid-email')) || '',
      requestError: (form && form.getAttribute('data-request-error')) || '',
      successTemplate: (form && form.getAttribute('data-success-template')) || ''
    };
  }

  // The id lives in the theme settings only; there is no copy of it here.
  function companyId(form) {
    var value = form.getAttribute('data-company-id');
    return value && value.trim() ? value.trim() : '';
  }

  /**
   * Klaviyo's JSON:API body for a back in stock subscription. The profile
   * carries the address and nothing else - no consent, no list, no
   * subscription - so the sign up is a restock alert only.
   */
  function buildPayload(email, variantId) {
    return {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: { email: email }
            }
          },
          channels: ['EMAIL']
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: VARIANT_PREFIX + variantId
            }
          }
        }
      }
    };
  }

  // A sign up that outlived its form: nothing is written onto the reopened form,
  // but the submit button it left disabled is handed back.
  function settleStale(form, state) {
    state.pending = false;
    if (!document.contains(form)) return;
    var nodes = parts(form);
    var usable = nodes.size && !nodes.size.disabled && nodes.size.value;
    setPending(nodes, false);
    if (!usable && nodes.submit) nodes.submit.setAttribute('disabled', 'disabled');
  }

  // A failed sign up is announced on a node the shopper may never reach by
  // reading order alone, so focus goes to it as a backstop to role="alert".
  function showFormError(nodes, message) {
    if (!nodes.formError) return;
    nodes.formError.textContent = message;
    nodes.formError.focus();
  }

  function submit(form) {
    var nodes = parts(form);
    var state = stateFor(form);
    var copy = strings(nodes);
    var email = nodes.email ? nodes.email.value.trim() : '';
    var variantId = nodes.size ? nodes.size.value : '';

    // One sign up at a time, whatever the button's disabled state says.
    if (state.pending) return;

    clearMessages(nodes);

    // Caught here, so a mistyped address never becomes a request the shopper
    // has to wait on, and the message sits on the field that is wrong.
    if (!EMAIL_PATTERN.test(email)) {
      if (nodes.emailError) nodes.emailError.textContent = copy.invalidEmail;
      if (nodes.email) {
        nodes.email.setAttribute('aria-invalid', 'true');
        nodes.email.focus();
      }
      return;
    }

    if (!variantId) {
      showFormError(nodes, copy.requestError);
      return;
    }

    var label = nodes.size.options[nodes.size.selectedIndex]
      ? nodes.size.options[nodes.size.selectedIndex].textContent
      : '';
    var product = productFor(variantId, displayedHandle());
    var titleNode = document.querySelector('.product-title');
    var productTitle = (product && product.title) ||
      (titleNode && titleNode.textContent.trim()) ||
      document.title;

    var company = companyId(form);
    if (!company) {
      if (window.console && window.console.warn) {
        window.console.warn('Notify Me: the Klaviyo company id theme setting is empty; the sign up was not sent.');
      }
      showFormError(nodes, copy.requestError);
      return;
    }

    setPending(nodes, true);
    state.pending = true;
    state.generation += 1;
    var generation = state.generation;

    window.fetch(ENDPOINT + '?company_id=' + encodeURIComponent(company), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: REVISION
      },
      body: JSON.stringify(buildPayload(email, variantId))
    }).then(function (response) {
      if (state.generation !== generation) {
        settleStale(form, state);
        return;
      }
      state.pending = false;
      if (response.status !== 202) throw new Error('klaviyo ' + response.status);

      nodes.form.setAttribute('hidden', '');
      if (nodes.success) {
        nodes.success.textContent = copy.successTemplate
          .replace('[[product]]', productTitle)
          .replace('[[size]]', label);
        nodes.success.focus();
      }
    }).catch(function () {
      if (state.generation !== generation) {
        settleStale(form, state);
        return;
      }
      state.pending = false;
      setPending(nodes, false);
      // Different message, different place, so a failed request is never read
      // as a bad address or as a sign up that worked.
      showFormError(nodes, copy.requestError);
    });
  }

  function onOpen(event) {
    // The dialog is still the button's to open; this only prepares what is in it.
    if (event.defaultPrevented) return;

    var scope = event.detail && event.detail.button ? event.detail.button.closest('product-form') : null;
    var form = (scope && scope.querySelector(FORM_SELECTOR)) || document.querySelector(FORM_SELECTOR);
    if (form) reset(form, event.detail);
  }

  function onSubmit(event) {
    var form = event.target && event.target.closest ? event.target.closest(FORM_SELECTOR) : null;
    if (!form) return;
    event.preventDefault();
    submit(form);
  }

  function onInput(event) {
    var field = event.target && event.target.closest ? event.target.closest('[data-notify-me-email]') : null;
    if (!field || field.getAttribute('aria-invalid') !== 'true') return;
    field.removeAttribute('aria-invalid');
    var form = field.closest(FORM_SELECTOR);
    var error = form && form.querySelector('[data-notify-me-email-error]');
    if (error) error.textContent = '';
  }

  function onVariantChanged(event) {
    if (event.detail && event.detail.productData) remember(event.detail.productData);
  }

  function refresh() {
    readInlineProducts();
  }

  function init() {
    readInlineProducts();
    document.addEventListener('notify-me:open', onOpen);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('input', onInput);
    document.addEventListener('product:variant-changed', onVariantChanged);
  }

  window.themeNotifyMeForm = {
    version: 1,
    refresh: refresh,
    buildPayload: buildPayload,
    endpoint: ENDPOINT,
    revision: REVISION
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
